import {
  isVerificationStepKey,
  VerificationCaseKindSchema,
  type VerificationCaseKind,
  type VerificationDecisionReason,
  type VerificationStepKey,
} from '@aysar/contracts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalData } from '@/shared/store/reducers/modal/modal.types';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { toApiError } from '@/shared/utils/transform-error/transform-error';

import { useDecideCaseMutation } from '../../services/verification/verification.api';
import { verificationErrorCopy } from '../../utils/verification-error/verification-error.util';

/** What the case screen hands either verdict dialog. Ids, an email and the kind only. */
export interface CaseVerdictTarget {
  caseId: string;
  /** `null` when the account lookup came back short — draw the absence (P14b). */
  member: string | null;
  /** Decides which reasons a refusal may carry (D6). Absent or unknown reads as a first look. */
  kind: VerificationCaseKind;
  /** Steps not yet satisfied — an approval over them is deliberate, and the dialog says so (F7). */
  outstandingSteps: readonly VerificationStepKey[];
}

export interface UseCaseVerdictLogic {
  target: CaseVerdictTarget | null;
  note: string;
  setNote: (value: string) => void;
  isDeciding: boolean;
  /** The api's refusal, already in this module's words. */
  error: string | null;
  /**
   * Sends the verdict.
   *
   * `reasonCode` is required for a rejection and meaningless for an approval —
   * the contract's discriminated union says so, and this signature keeps the
   * same shape rather than making both optional and hoping. A rejection called
   * without one does nothing: the button that would do it is disabled until a
   * reason is picked, so reaching here is a bug, not a state to guess at.
   */
  submit: (reasonCode?: VerificationDecisionReason) => Promise<void>;
}

/**
 * The behaviour behind both verdicts.
 *
 * Shared because the *mechanics* are identical — one mutation, one note, one set
 * of refusals — while the two dialogs themselves are separate components,
 * because what they show is not. That split is deliberate: a single component
 * branching on the action would carry a reason picker and a member-facing
 * preview that are inert on the approve branch, which is the
 * silently-does-nothing shape `ComboboxFieldDef`'s own doc names as the reason it
 * is not a flag on `select`.
 *
 * The note is sent as `null` when blank rather than as `''`: `VerificationDecisionSchema`
 * types it `.trim().min(1).nullable()`, so an empty string is a refusal on a
 * field the reviewer deliberately left alone.
 *
 * `verification_case_changed` gets its own sentence. A colleague deciding first
 * is not a fault and must not be reported as one — the reviewer needs to be told
 * to re-read the case, which the screen behind this dialog is already doing.
 */
export function useCaseVerdictLogic(
  action: 'approve' | 'reject',
  modalData: ModalData | undefined,
  onClose: () => void,
): UseCaseVerdictLogic {
  const { t } = useTranslation('verification');
  const toast = useToast();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [decideCase, { isLoading: isDeciding }] = useDecideCaseMutation();

  const target = readTarget(modalData);

  const submit = useCallback(
    async (reasonCode?: VerificationDecisionReason) => {
      if (target === null) return;

      setError(null);
      const trimmed = note.trim();
      // `null`, never `''`. `VerificationDecisionSchema` types the note
      // `.trim().min(1).nullable()`, so an empty string is a refusal on a field
      // the reviewer deliberately left alone.
      const note_ = trimmed === '' ? null : trimmed;

      try {
        if (action === 'approve') {
          await decideCase({ id: target.caseId, action: 'approve', note: note_ }).unwrap();
        } else {
          // An `if` rather than a ternary, so the compiler narrows `reasonCode`
          // itself instead of being told to with a cast. Reaching here without
          // one is a bug — the submit button stays disabled until a reason is
          // picked — so it does nothing rather than sending a verdict the member
          // would read as blank.
          if (reasonCode === undefined) return;

          await decideCase({
            id: target.caseId,
            action: 'reject',
            reasonCode,
            note: note_,
          }).unwrap();
        }

        toast.success(t(action === 'approve' ? 'approve.success' : 'reject.success'));
        onClose();
      } catch (caught) {
        const copy = verificationErrorCopy(toApiError(caught));
        setError(copy === null ? t('error.generic') : t(copy.i18nKey));
      }
    },
    [action, decideCase, note, onClose, t, target, toast],
  );

  return { target, note, setNote, isDeciding, error, submit };
}

/**
 * Narrows the modal payload.
 *
 * `ModalData` is `Record<string, unknown>` by design — it survives a reload and a
 * serialization boundary — so every field is checked rather than cast. A dialog
 * that trusted the payload would decide whatever case id happened to be in it.
 */
function readTarget(data: ModalData | undefined): CaseVerdictTarget | null {
  if (data === undefined) return null;

  const caseId = str(data['caseId']);
  if (caseId === null) return null;

  const kind = VerificationCaseKindSchema.safeParse(data['kind']);
  const outstanding = data['outstandingSteps'];

  return {
    caseId,
    member: str(data['member']),
    kind: kind.success ? kind.data : 'initial',
    // Comma-joined, because `ModalData` carries scalars only; unknown keys are dropped.
    outstandingSteps:
      typeof outstanding === 'string' && outstanding !== ''
        ? outstanding.split(',').filter(isVerificationStepKey)
        : [],
  };
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}
